/**
 * Step 5b: choose the new password.
 *
 * The token arrives in the query string, put there by Better Auth's redirect.
 * It is single-use, expires in an hour, and using it revokes every session
 * the account had — all three are configured on the server, not enforced
 * here; this page only carries the token back.
 */

import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { resetPasswordInputSchema } from '../../shared/auth-schemas.ts'
import type { MessageKey } from '../../shared/message-keys.ts'
import { Alert } from '../components/Alert.tsx'
import { Field } from '../components/Field.tsx'
import { useI18n } from '../i18n/index.tsx'
import { messageKeyFor } from '../lib/api.ts'
import { authClient } from '../lib/auth-client.ts'
import { useClientConfig } from '../lib/use-client-config.ts'

export function ResetPassword() {
  const { t } = useI18n()
  const config = useClientConfig()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [error, setError] = useState<MessageKey | null>(
    params.has('error') ? 'error.token_invalid' : null,
  )
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    const parsed = resetPasswordInputSchema.safeParse({ token, password })
    if (!parsed.success) {
      setError((parsed.error.issues[0]?.message ?? 'error.bad_request') as MessageKey)
      return
    }

    setBusy(true)
    const { error: failure } = await authClient.resetPassword({
      newPassword: parsed.data.password,
      token: parsed.data.token,
    })
    setBusy(false)

    if (failure) {
      setError(messageKeyFor(failure))
      return
    }
    setDone(true)
  }

  if (done) {
    return (
      <section className="panel">
        <h1>{t('reset.success.title')}</h1>
        <p>{t('reset.success.body')}</p>
        <Link to="/signin" className="button">
          {t('signin.submit')}
        </Link>
      </section>
    )
  }

  return (
    <section className="panel">
      <h1>{t('reset.title')}</h1>
      <p>{t('reset.lead')}</p>

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          label={t('reset.password.label')}
          hint={t('register.password.hint')}
          type="password"
          name="password"
          autoComplete="new-password"
          minLength={config?.passwordMinLength ?? 12}
          required
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
          }}
        />

        {error ? <Alert tone="error">{t(error)}</Alert> : null}

        <button type="submit" className="button" disabled={busy || !token}>
          {t('reset.submit')}
        </button>
      </form>
    </section>
  )
}
